import React, { forwardRef } from 'react'
import { makeStyles } from '@mui/styles'
import theme from '@/theme'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import BankingLogo from '@/modules/banking/components/banking-logo'
import lang from '@/lang'
import { formatIban } from '@/modules/_helpers/formatIban'
import ToastFrame from './toast-frame'
import type { SubscribeToToastStack, ToastKey } from './use-toast-controller'

// icons for states
import SuccessIcon from '@assets/images/icons/check.svg'
import WarningIcon from '@assets/images/icons/exclamation_mark2.svg'
import ErrorIcon from '@assets/images/icons/cancel.svg'
import HandshakeIcon from '@assets/images/notifications/handshake.svg'

const { messages: { common: { toastFinanceHeadings: headings, toastHeadings } } } = lang

type ToastType = 'success' | 'warning' | 'error' | 'info'
type SvgComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>

interface ToastIconSetting {
  file: SvgComponent
  width: string | number
  height: string | number
  fill: string
}

interface ToastSetting {
  icon: ToastIconSetting
  heading: string
  accentColor: string
  accentColorBG: string
  iconBubbleBackground: string
}

type ToastSettings = Record<ToastType, ToastSetting>

interface ToastBankingTemplateProps {
  snackBarKey: ToastKey
  title?: string
  type: ToastType
  message?: string
  bankLogoUrl: string
  bicCode?: string
  bankName?: string
  showOnlyBankName?: boolean
  accountIban: string
  accountName: string
  autoHideDuration?: number | null
  subscribeToToastStack?: SubscribeToToastStack
  removeToast?: (key: ToastKey) => void
}

interface ToastBankingTemplateStyleProps {
  toastSettings: ToastSettings
  type: ToastType
}

const getToastSettings = (infoIcon: ToastIconSetting): ToastSettings => ({
  success: {
    icon: {
      file: SuccessIcon,
      width: '10px',
      height: '10px',
      fill: theme.white,
    },
    heading: headings.success,
    accentColor: theme.green,
    accentColorBG: theme.greenBackground,
    iconBubbleBackground: theme.green,
  },
  warning: {
    icon: {
      file: WarningIcon,
      width: '10px',
      height: '10px',
      fill: theme.white,
    },
    heading: headings.warning,
    accentColor: theme.orange,
    accentColorBG: theme.orangeBackground,
    iconBubbleBackground: theme.orange,
  },
  error: {
    icon: {
      file: ErrorIcon,
      width: '10px',
      height: '10px',
      fill: theme.white,
    },
    heading: headings.error,
    accentColor: theme.error,
    accentColorBG: theme.redBackground,
    iconBubbleBackground: theme.error,
  },
  info: {
    icon: { ...infoIcon },
    heading: headings.info,
    accentColor: theme.lighterBlue,
    accentColorBG: theme.lighterBlueBackground,
    iconBubbleBackground: theme.white,
  },
})

const useStyles = makeStyles(() => ({
  header: {
    fontSize: theme.fontSizes.medium,
    color: theme.textColor,
    marginBottom: 11,
  },
  bankSection: {
    display: 'flex',
    width: '100%',
    border: theme.border,
    borderRadius: theme.borderRadius,
    padding: 5,
    alignItems: 'center',
  },
  bankLogo: {
    paddingRight: 10,
    paddingLeft: 10,
  },
  bankInfoSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  bankMessageListHeader: {
    paddingTop: 15,
    paddingBottom: 10,
  },
  bankMessage: ({ toastSettings, type }: ToastBankingTemplateStyleProps) => {
    return {
      width: '100%',
      padding: '10px 5px 10px 5px',
      marginBottom: 10,
      display: 'flex',
      alignItems: 'center',
      color: toastSettings[type].accentColor,
      backgroundColor: toastSettings[type].accentColorBG,
      border: toastSettings[type].accentColorBG,
      borderRadius: theme.borderRadius,
    }
  },
  help: {
    paddingBottom: 5,
  },
  accountName: {
    fontWeight: 'bold',
  },
  bankMessageIconBubble: ({ toastSettings, type }: ToastBankingTemplateStyleProps) => {
    return {
      borderRadius: '50%',
      backgroundColor: toastSettings[type].iconBubbleBackground,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 10,
      marginRight: 10,
    }
  },
}))

const ToastBankingTemplate = forwardRef<HTMLElement, ToastBankingTemplateProps>((data, ref) => {
  const { title, bankLogoUrl, bankName, bicCode, showOnlyBankName = false, accountIban, accountName, snackBarKey, message = headings.unknowError, type, autoHideDuration, subscribeToToastStack, removeToast } = data
  const toastSettings = getToastSettings({
    file: HandshakeIcon,
    width: 25,
    height: 25,
    fill: theme.lightGrey,
  })
  const classes = useStyles({ toastSettings, type })

  const heading = title || (
    type === 'info'
      ? toastHeadings[type]
      : null
  )

  const infoSelection = showOnlyBankName
    ? (<>
      <div className={classes.accountName}>{bankName}</div>
      <div>{bicCode}</div>
    </>)
    : (<>
      <div className={classes.accountName}>{accountName}</div>
      <div>{formatIban(accountIban)}</div>
    </>)

  return (
    <ToastFrame
      ref={ref}
      snackBarKey={snackBarKey}
      type={type}
      accentColor={toastSettings[type].accentColor}
      autoHideDuration={autoHideDuration}
      subscribeToToastStack={subscribeToToastStack}
      removeToast={removeToast}
      variant='banking'
    >
      {heading && <div className={classes.header}>{heading}</div>}
      <div className={classes.bankSection}>
        <div className={classes.bankLogo}>
          <BankingLogo url={bankLogoUrl} height='25px' />
        </div>
        <div className={classes.bankInfoSection}>
          {infoSelection}
        </div>
      </div>
      <div className={classes.bankMessageListHeader}>{headings.messageHeaders[type]}</div>
      <div className={classes.bankMessage}>
        {toastSettings[type].icon.file
          ? (<div className={classes.bankMessageIconBubble}><SVGIcon
            svg={toastSettings[type].icon.file}
            height={toastSettings[type].icon.height}
            width={toastSettings[type].icon.width}
            fill={toastSettings[type].icon.fill}
          /></div>)
          : null
        }
        <span>{message}</span>
      </div>
      <div className={classes.help}>{headings.helpText[type]}</div>
    </ToastFrame>
  )
})

export default ToastBankingTemplate
